import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';
import { OverflowList } from '@theme/critical';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // Get all selected options before update
    const previousSelectedOptions = this.selectedOptionsValues;
    
    this.updateSelectedOption(event.target);
    
    // Get all selected options after update
    const currentSelectedOptions = this.selectedOptionsValues;
    
    const changedOptionId = selectedOption.dataset.optionValueId;
    const changedOptionValue = selectedOption.value || selectedOption.textContent;
    const changedOptionName = event.target.closest('fieldset')?.querySelector('legend')?.textContent?.trim() || 
                              event.target.closest('.variant-option')?.querySelector('label')?.textContent?.trim() || 
                              'Unknown';
    
    const variantId = selectedOption.dataset.variantId || null;
    const normalizedOptionName = changedOptionName.toLowerCase();
    const isSizeOption = normalizedOptionName.includes('size');
    const isColorOption = normalizedOptionName.includes('color') || normalizedOptionName.includes('colour');
    
    // Check if we're on product page (needed for auto-size selection logic)
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    if (isOnProductPage && isColorOption && window.innerWidth < 750) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // If variantId is null and changed option is NOT size, find and select next available size
    if (!variantId && !isSizeOption) {
      const sizeFieldset = this.querySelector('.variant-option--size fieldset, .variant-option.variant-option--size');
      
      if (sizeFieldset) {
        // Find all size inputs
        const sizeInputs = Array.from(sizeFieldset.querySelectorAll('input[type="radio"]'));
        
        // Find the first available size option
        const availableSizeInput = sizeInputs.find(input => {
          const isAvailable = input.dataset.optionAvailable === 'true';
          const isNotCurrentlySelected = !input.checked;
          return isAvailable && isNotCurrentlySelected;
        });
        
        if (availableSizeInput) {
          // Check the input and trigger change event
          availableSizeInput.checked = true;
          
          // Also update the selected option state
          this.updateSelectedOption(availableSizeInput);
          
          // Dispatch variant selected event for the size option
          this.dispatchEvent(new VariantSelectedEvent({ id: availableSizeInput.dataset.optionValueId ?? '' }));
          
          // Continue with the rest of the logic for the size selection
          const sizeVariantId = availableSizeInput.dataset.variantId || null;
          
          if (isOnProductPage) {
            const url = new URL(window.location.href);
            if (sizeVariantId) {
              url.searchParams.set('variant', sizeVariantId);
            } else {
              url.searchParams.delete('variant');
            }
            
            if (url.href !== window.location.href) {
              requestYieldCallback(() => {
                history.replaceState({}, '', url.toString());
              });
            }
          }
          
          // Fetch updated section for the size selection
          this.fetchUpdatedSection(this.buildRequestUrl(availableSizeInput), false);
          
          return; // Exit early since we've handled the size selection
        }
      }
    }
    
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // Preserve disabled state of overflow lists before morphing
    const overflowLists = this.querySelectorAll('overflow-list');
    const preservedStates = new Map();
    for (const list of overflowLists) {
      if (list.hasAttribute('disabled') && list.getAttribute('disabled') === 'true') {
        preservedStates.set(list, true);
      }
    }

    // Find and preserve fit-quiz-wrapper sibling before morphing
    let currentFitQuizWrapper = null;
    const nextSibling = this.nextElementSibling;
    if (nextSibling instanceof HTMLElement && 
        nextSibling.classList.contains('shopify-block') && 
        nextSibling.classList.contains('shopify-app-block') &&
        nextSibling.classList.contains('fit-quiz-wrapper')) {
      currentFitQuizWrapper = nextSibling;
    }

    // Find fit-quiz-wrapper in new HTML (search for it after variant-picker in the document)
    let newFitQuizWrapper = null;
    // Try to find it as a sibling in the new HTML
    const newVariantPickerParent = newVariantPickerSource.parentElement;
    if (newVariantPickerParent) {
      const allSiblings = Array.from(newVariantPickerParent.children);
      const variantPickerIndex = allSiblings.indexOf(newVariantPickerSource);
      if (variantPickerIndex >= 0 && variantPickerIndex < allSiblings.length - 1) {
        const potentialFitQuiz = allSiblings[variantPickerIndex + 1];
        if (potentialFitQuiz instanceof HTMLElement &&
            potentialFitQuiz.classList.contains('shopify-block') &&
            potentialFitQuiz.classList.contains('shopify-app-block') &&
            potentialFitQuiz.classList.contains('fit-quiz-wrapper')) {
          newFitQuizWrapper = potentialFitQuiz;
        }
      }
    }
    
    // Fallback: search entire document for fit-quiz-wrapper
    if (!newFitQuizWrapper) {
      const fitQuizInNewHtml = newHtml.querySelector('.shopify-block.shopify-app-block.fit-quiz-wrapper');
      if (fitQuizInNewHtml instanceof HTMLElement) {
        newFitQuizWrapper = fitQuizInNewHtml;
      }
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Morph the variant picker
    morph(this, newVariantPickerSource);

    // Morph the fit-quiz-wrapper sibling if it exists in both old and new HTML
    if (currentFitQuizWrapper && newFitQuizWrapper) {
      morph(currentFitQuizWrapper, newFitQuizWrapper);
    } else if (newFitQuizWrapper && !currentFitQuizWrapper) {
      // New HTML has fit-quiz-wrapper but current doesn't - insert it
      // Clone the element since it's from the parsed HTML document
      const clonedFitQuiz = newFitQuizWrapper.cloneNode(true);
      this.insertAdjacentElement('afterend', clonedFitQuiz);
    } else if (currentFitQuizWrapper && !newFitQuizWrapper) {
      // Current HTML has fit-quiz-wrapper but new doesn't - remove it
      currentFitQuizWrapper.remove();
    }

    // Restore disabled state immediately after morph
    for (const [list, wasDisabled] of preservedStates) {
      if (wasDisabled && list instanceof HTMLElement && list.isConnected) {
        // Find the corresponding list in the morphed DOM
        const refAttr = list.getAttribute('ref');
        const newListElement = refAttr 
          ? this.querySelector(`overflow-list[ref="${refAttr}"]`)
          : Array.from(this.querySelectorAll('overflow-list')).find(l => 
              l instanceof HTMLElement && l !== list && !l.hasAttribute('disabled')
            );
        
        const newList = newListElement instanceof OverflowList ? newListElement : null;
        const oldList = list instanceof OverflowList ? list : null;
        
        if (newList) {
          newList.setAttribute('disabled', 'true');
          requestAnimationFrame(() => {
            if (newList.shadowRoot) {
              newList.showAll();
            } else {
              setTimeout(() => {
                if (newList.shadowRoot) {
                  newList.showAll();
                }
              }, 100);
            }
          });
        }
      }
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
